import { useCallback, useRef, useState } from 'react';

const TOTAL_PAGES = 604;
const BATCH_SIZE = 10;

type Status = 'idle' | 'counting' | 'downloading' | 'done' | 'error';

interface OfflineCacheState {
  status: Status;
  cached: number;
  total: number;
  percent: number;
  error?: string;
}

async function countCachedPages(): Promise<number> {
  const versesCache = await caches.open('haamil-verses');
  const keys = await versesCache.keys();
  const pages = new Set(
    keys.map((req) => {
      const match = req.url.match(/\/data\/verses\/(\d+)\.json/);
      return match ? Number(match[1]) : 0;
    }),
  );
  pages.delete(0);
  return pages.size;
}

async function downloadBatch(
  pages: number[],
  versesCache: Cache,
  fontsCache: Cache,
) {
  await Promise.all(
    pages.map(async (page) => {
      const verseUrl = `/data/verses/${page}.json`;
      const fontUrl = `/fonts/hafs/v1/woff2/p${page}.woff2`;

      const [verseResp, fontResp] = await Promise.all([
        fetch(verseUrl),
        fetch(fontUrl),
      ]);

      if (verseResp.ok) await versesCache.put(verseUrl, verseResp);
      if (fontResp.ok) await fontsCache.put(fontUrl, fontResp);
    }),
  );
}

export function useOfflineCache() {
  const [state, setState] = useState<OfflineCacheState>({
    status: 'idle',
    cached: 0,
    total: TOTAL_PAGES,
    percent: 0,
  });
  const abortRef = useRef<AbortController | null>(null);

  const refreshCount = useCallback(async () => {
    setState((s) => ({ ...s, status: 'counting' }));
    const cached = await countCachedPages();
    setState({
      status: cached >= TOTAL_PAGES ? 'done' : 'idle',
      cached,
      total: TOTAL_PAGES,
      percent: Math.round((cached / TOTAL_PAGES) * 100),
    });
  }, []);

  const downloadAll = useCallback(async () => {
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    try {
      const versesCache = await caches.open('haamil-verses');
      const fontsCache = await caches.open('haamil-page-fonts');
      const existingKeys = await versesCache.keys();
      const cachedPages = new Set(
        existingKeys.map((req) => {
          const match = req.url.match(/\/data\/verses\/(\d+)\.json/);
          return match ? Number(match[1]) : 0;
        }),
      );

      const remaining = Array.from(
        { length: TOTAL_PAGES },
        (_, i) => i + 1,
      ).filter((p) => !cachedPages.has(p));

      let downloaded = cachedPages.size;

      setState({
        status: 'downloading',
        cached: downloaded,
        total: TOTAL_PAGES,
        percent: Math.round((downloaded / TOTAL_PAGES) * 100),
      });

      for (let i = 0; i < remaining.length; i += BATCH_SIZE) {
        if (signal.aborted) return;
        const batch = remaining.slice(i, i + BATCH_SIZE);
        await downloadBatch(batch, versesCache, fontsCache);
        downloaded += batch.length;
        setState({
          status: 'downloading',
          cached: downloaded,
          total: TOTAL_PAGES,
          percent: Math.round((downloaded / TOTAL_PAGES) * 100),
        });
      }

      // Also cache metadata and font CSS
      const metadataCache = await caches.open('haamil-metadata');
      const fontCssCache = await caches.open('haamil-font-css');
      await Promise.all([
        fetch('/data/parts.json').then((r) =>
          r.ok ? metadataCache.put('/data/parts.json', r) : undefined,
        ),
        fetch('/data/chapters.json').then((r) =>
          r.ok ? metadataCache.put('/data/chapters.json', r) : undefined,
        ),
        fetch('/fonts/fonts-hafs-v1.css').then((r) =>
          r.ok ? fontCssCache.put('/fonts/fonts-hafs-v1.css', r) : undefined,
        ),
      ]);

      setState({
        status: 'done',
        cached: TOTAL_PAGES,
        total: TOTAL_PAGES,
        percent: 100,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'QuotaExceededError') {
        const cached = await countCachedPages();
        setState({
          status: 'error',
          cached,
          total: TOTAL_PAGES,
          percent: Math.round((cached / TOTAL_PAGES) * 100),
          error: 'Storage full. Free up space and try again.',
        });
      } else if (err instanceof DOMException && err.name === 'AbortError') {
        // Cancelled
      } else {
        const cached = await countCachedPages();
        setState({
          status: 'error',
          cached,
          total: TOTAL_PAGES,
          percent: Math.round((cached / TOTAL_PAGES) * 100),
          error: 'Download failed. Check your connection and try again.',
        });
      }
    }
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    void refreshCount();
  }, [refreshCount]);

  return { ...state, downloadAll, cancel, refreshCount };
}
